import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, Button, Table } from 'reactstrap';
import { Link, useParams, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import axios from 'axios';
import UploadModal from './components/UploadModal';

const ImportConfigurationView = ({ organization, theme, rtl }) => {
    const { id } = useParams();
    const [config, setConfig] = useState(null);
    const [imports, setImports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isUploadModalOpen, setUploadModalOpen] = useState(false);
    const [runningIds, setRunningIds] = useState({});
    const isQa = process.env.REACT_APP_SENTRY_ENVIRONMENT !== 'production';

    const fetchImports = () => {
        setLoading(true);
        axios.get(`/organization/${organization.id}/import`, {
            params: { csv_import_setting_id: id, limit: 10 }
        })
        .then(res => setImports(res.data?.data || []))
        .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (id && organization?.id) {
            axios.get(`/organization/${organization.id}/csv-import-setting/${id}/show`)
                .then(res => setConfig(res.data));
            fetchImports();
        }
    }, [id, organization?.id]);

    const runImport = async (importId) => {
        setRunningIds(prev => ({ ...prev, [importId]: true }));
        try {
            await axios.post(`/organization/${organization.id}/import/${importId}/run`);
            const poll = setInterval(async () => {
                const res = await axios.get(`/organization/${organization.id}/import`, {
                    params: { csv_import_setting_id: id, limit: 10 }
                });
                const updated = res.data?.data || [];
                setImports(updated);
                const item = updated.find(i => i.id === importId);
                if (item?.is_imported) {
                    clearInterval(poll);
                    setRunningIds(prev => ({ ...prev, [importId]: false }));
                }
            }, 5000);
        } catch (e) {
            setRunningIds(prev => ({ ...prev, [importId]: false }));
        }
    };

    if (!config) return <p>Loading...</p>;

    return (
        <>
        {isUploadModalOpen && (
            <UploadModal
                organization={organization}
                isOpen={isUploadModalOpen}
                toggle={() => setUploadModalOpen(false)}
                setOpen={setUploadModalOpen}
                config={config}
                theme={theme || { className: '' }}
                rtl={rtl || { direction: 'ltr' }}
                onSuccess={fetchImports}
            />
        )}
        <Container className="dashboard">
            <Row>
                <Col md={12}>
                    <h3 className="page-title">
                        Import Configuration
                    </h3>
                    <h3 className="page-subhead subhead">
                        <Link to="/">Home</Link> /&nbsp;
                        <Link to="/import/configurations">Import Configurations</Link> /&nbsp;
                        #{id}
                    </h3>
                </Col>
            </Row>

            <Row>
                {/* Left panel — name + buttons + settings */}
                <Col md={8}>
                    <Card className="h-100">
                        <CardBody>
                            <h4 className="mb-3">{config.name || `Configuration #${id}`}</h4>
                            <table className="table table-sm">
                                <tbody>
                                    <tr>
                                        <th style={{width: 160}}>Import Type</th>
                                        <td>{config.csv_import_types_name || '—'} ({config.csv_import_types_context || '—'})</td>
                                    </tr>
                                    <tr>
                                        <th>Root Program</th>
                                        <td>
                                            {config.root_program_id
                                                ? <Link to={`/program/${config.root_program_id}/view`}>
                                                    #{config.root_program_id} {config.root_program_name ? `— ${config.root_program_name}` : ''}
                                                  </Link>
                                                : '—'}
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Owner Program</th>
                                        <td>
                                            {config.owner_program_id
                                                ? <><Link to={`/program/${config.owner_program_id}/view`}>
                                                    #{config.owner_program_id} {config.owner_program_name ? `— ${config.owner_program_name}` : ''}
                                                  </Link>
                                                  <small className="text-muted d-block">Set from manager side</small></>
                                                : <><span>—</span>
                                                  <small className="text-muted d-block">Set automatically when import is configured from a program on the manager side</small></>
                                            }
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Active Feed</th>
                                        <td>
                                            {config.active_feed_id
                                                ? <Link to={`/import/source/edit/${config.active_feed_id}`}>
                                                    {config.active_feed_name || `#${config.active_feed_id}`}
                                                  </Link>
                                                : <span className="text-muted">Manually</span>
                                            }
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </CardBody>
                    </Card>
                </Col>

                {/* Right panel — buttons + #id, date, created by */}
                <Col md={4}>
                    <Card className="h-100">
                        <CardBody>
                            <div className="d-flex gap-2 justify-content-end mb-3">
                                <Button color="success" size="sm" onClick={() => setUploadModalOpen(true)}>Import</Button>
                                <Link to={`/import/configurations/${id}/edit`} className="btn btn-primary btn-sm">Edit</Link>
                            </div>
                            <table className="table table-sm">
                                <tbody>
                                    <tr>
                                        <th style={{width: 100}}>ID</th>
                                        <td>#{config.id}</td>
                                    </tr>
                                    <tr>
                                        <th>Created</th>
                                        <td>{config.created_at ? new Date(config.created_at).toLocaleString() : '—'}</td>
                                    </tr>
                                    <tr>
                                        <th>Created By</th>
                                        <td>
                                            {config.created_by_user
                                                ? <span>{config.created_by_user.first_name} {config.created_by_user.last_name}<br/><small>{config.created_by_user.email}</small></span>
                                                : <span className="text-muted">—</span>
                                            }
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Row className="mt-3">
                <Col md={12}>
                    <Card>
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="mb-0">Import History</h5>
                                <Link to={`/import/list?config=${id}`} className="btn btn-sm btn-outline-secondary">
                                    View all →
                                </Link>
                            </div>
                            {loading && <p>Loading...</p>}
                            {!loading && imports.length === 0 && <p className="text-muted">No imports yet.</p>}
                            {!loading && imports.length > 0 && (
                                <Table size="sm" className="table table--bordered">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>File Name</th>
                                            <th>Rows</th>
                                            <th>Filesize</th>
                                            <th>Processed?</th>
                                            <th>Imported?</th>
                                            <th>Uploaded By</th>
                                            <th>Date</th>
                                            {isQa && <th></th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {imports.map(item => (
                                            <tr key={item.id}>
                                                <td>{item.id}</td>
                                                <td>
                                                    <Link to={`/import/view/${item.id}`}>{item.name}</Link>
                                                </td>
                                                <td>{item.rowcount}</td>
                                                <td>{item.size}</td>
                                                <td>{item.is_processed}</td>
                                                <td>{item.is_imported}</td>
                                                <td>
                                                    {item.created_by_user
                                                        ? <span>{item.created_by_user.first_name} {item.created_by_user.last_name}<br/><small>{item.created_by_user.email}</small></span>
                                                        : <span className="text-muted">Unknown</span>
                                                    }
                                                </td>
                                                <td>{item.created_at ? new Date(item.created_at).toLocaleString() : '—'}</td>
                                                {isQa && (
                                                    <td>
                                                        {!item.is_imported && (
                                                            <button
                                                                onClick={() => runImport(item.id)}
                                                                disabled={!!runningIds[item.id]}
                                                                style={{
                                                                    backgroundColor: '#28a745',
                                                                    color: '#fff',
                                                                    border: 'none',
                                                                    borderRadius: 4,
                                                                    padding: '3px 10px',
                                                                    fontSize: 12,
                                                                    cursor: runningIds[item.id] ? 'not-allowed' : 'pointer',
                                                                    opacity: runningIds[item.id] ? 0.7 : 1,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 4,
                                                                }}
                                                            >
                                                                {runningIds[item.id]
                                                                    ? <span style={{ display: 'inline-block', width: 10, height: 10, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
                                                                    : <span>▶</span>
                                                                }
                                                                {runningIds[item.id] ? 'Running...' : 'Run'}
                                                            </button>
                                                        )}
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Container>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </>
    );
};

export default withRouter(connect((state) => ({
    organization: state.organization,
    theme: state.theme,
    rtl: state.rtl,
}))(ImportConfigurationView));
